/**
 * DetailSettingsSection.tsx
 * メール送信フラグ, JSONデータボタン, 事前受付リンク, 予約日, 予約回数
 * ※ 請求書操作（発行/Squareで開く/取消/invoice ID）は 2026-07 に
 *   基本情報セクションの SquarePaymentInfo（Square請求書ボックス）へ移設
 */
import React from 'react';
import { SectionProps } from '../types';
import SectionHeader from '../shared/SectionHeader';
import FieldDisplay from '../shared/FieldDisplay';
import { isoToLocalInput } from '../../../lib/dateUtils';
import { getSiteOrigin } from '../../../config/backend';
import { FieldMarkWrap } from '../conflict/fieldState';
import { formUrl } from '../../../lib/customerLinks';

const JSON_FIELDS = ['json_data'] as const;

interface DetailSettingsSectionProps extends SectionProps {
  onJsonModalOpen?: () => void;
}

export default function DetailSettingsSection({
  data,
  onChange,
  disabled,
  isNew,
  onJsonModalOpen,
}: DetailSettingsSectionProps) {
  const bookingDateDisplay = (isoToLocalInput(data.booking_date) || '').replace('T', ' ') || '-';

  return (
    <>
      <SectionHeader title="詳細設定" />

      {/* メール送信フラグ (hidden when isNew) */}
      {!isNew && (
        <div className="gc-field">
          <label className="gc-label" style={{ fontSize: '0.85rem', color: '#64748b' }}>
            メール送信フラグ
          </label>
          <div style={{ display: 'flex', gap: 16, padding: '4px 0' }}>
            {[
              { field: 'mail_send_flg1', label: '予約確定' },
              { field: 'mail_send_flg2', label: '決済御礼' },
              { field: 'mail_send_flg3', label: '利用御礼' },
            ].map((flg) => (
              <FieldMarkWrap key={flg.field} fields={[flg.field]} inline>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: disabled ? 'default' : 'pointer', fontSize: '0.85rem' }}>
                  <input
                    type="checkbox"
                    checked={!!data[flg.field]}
                    onChange={(e) => onChange?.({ [flg.field]: e.target.checked })}
                    disabled={disabled}
                    style={{ width: 16, height: 16 }}
                  />
                  {flg.label}
                </label>
              </FieldMarkWrap>
            ))}
          </div>
        </div>
      )}

      {/* JSONデータボタン */}
      {!isNew && onJsonModalOpen && (
        <FieldMarkWrap fields={JSON_FIELDS} chip style={{ padding: '4px 0' }}>
          <button
            type="button"
            onClick={onJsonModalOpen}
            disabled={disabled}
            style={{
              padding: '4px 12px',
              fontSize: '0.75rem',
              border: '1px solid #cbd5e1',
              borderRadius: 4,
              background: '#fff',
              color: '#475569',
              cursor: disabled ? 'default' : 'pointer',
            }}
          >
            その他データ
          </button>
        </FieldMarkWrap>
      )}

      {/* 事前受付リンク (read-only)。オリジンは店舗別(那覇=n-car.jp / 宮古=pit-rent.com)。
          予約番号で作る（連番 ID の ?id= はページ側で受け付けない・2026-10-12） */}
      {data.id && data.contact_code && (
        <FieldDisplay
          label="事前受付リンク"
          value={formUrl(getSiteOrigin(), data.contact_code)}
        />
      )}

      {/* 予約日 (read-only) */}
      <FieldDisplay label="予約日" value={bookingDateDisplay} />

      {/* 予約回数 (read-only) */}
      <FieldDisplay
        label="来店回数"
        value={data.past_reservation_count ?? 0}
        suffix="回"
      />
    </>
  );
}
